/* eslint-disable max-len */
import React, { Suspense } from 'react';

import Layout from 'components/Layouts/AppLayout';

import { UserRole } from 'store/user/types/state.types';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import RequireAuth from 'components/Auth/RequireAuth';
import PageLoader from 'components/Loaders/PageLoader';
import StorageLayout from 'components/Layouts/StorageLayout/StorageLayout';
import KubernetesLayout from 'components/Layouts/KubernetesLayout/KubernetesLayout';
import LoadbalancerLayout from 'components/Layouts/LoadbalancerLayout/LoadbalancerLayout';
import CloudPlatform from 'pages/customer/coud-platform/CloudPlatform';
import IsServiceEnabled from 'components/Auth/IsServiceEnabled/IsServiceEnabled';
import { ServiceCode } from 'store/user/types/service';
import vmService from 'services/api/customer/vm-service';
import { IVMResBody } from 'store/virtual-machine/types/api-bodies.types';
import RequirePermission from 'components/Auth/RequirePermission';
import {
  PermissionLevel,
  ServicePermissions,
} from 'components/Project/types/enums';
import kubernetesService from 'services/api/customer/kubernetes-service';
import { IGetClusterByIdDto } from 'components/Kubernetes/types/dtos';
import { IWorkerNodesDto } from 'components/Kubernetes/types/worker-node';
import i18n from 'services/i18n';
import lazyRetry from 'routes/utils/lazy-retry';
import databaseService from 'services/api/customer/database-service';
import { DatabaseResponseDTO } from 'components/Database/types/dtos';
import DatabaseLayout from 'components/Layouts/DatabaseLayout/DatabaseLayout';
import { encodeObjectKey } from 'utils/formatters';
import networkService from 'services/api/customer/network-service';
import { IRouterDtoResponse } from 'store/network/types/api-bodies.types';
import autoscalingGroupsService from 'services/api/customer/autoscaling-groups-service';
import { IAsgResDtoResponse } from 'components/AutoscalingGroups/types/api-bodies.types';
import resolveProjectContext from 'routes/utils/resolve-project-context';

const LBTablePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/loadbalancers/Loadbalancers.Table')),
);
const LBCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/loadbalancers/Loadbalancers.Create')),
);
const LBEditPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/loadbalancers/Loadbalancers.Edit')),
);

const VMTablePage = React.lazy(() => lazyRetry(() => import('pages/customer/VM/vm.table')));
const VMCreatePage = React.lazy(() => lazyRetry(() => import('pages/customer/VM/vm.create')));
const VMRestoreFromBackupPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/VM/vm.restore')),
);
const VMInfoPage = React.lazy(() => lazyRetry(() => import('pages/customer/VM/vm.info')));
const VMEditPage = React.lazy(() => lazyRetry(() => import('pages/customer/VM/vm.edit')));

const NetworksPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/networks/Table.networks')),
);
// const NetworkCreatePage = React.lazy(() => (import ('pages/customer/networks/Create.networks')));

const DisksPage = React.lazy(() => lazyRetry(() => import('pages/customer/disks/Table.disks')));

const BackupsPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/backups/Table.backups')),
);
const OSTemplatesPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/templates/templates.table')),
);

const ReportsPage = React.lazy(() => lazyRetry(() => import('pages/customer/report/report')));
// const SupportPage = React.lazy(() => (import ('pages/customer/support/support')));

const LogPage = React.lazy(() => lazyRetry(() => import('pages/customer/settings/log/log')));
const ChangePasswordPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/change-pass/ChangePassPage')),
);
const TwoFAPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/two-fa/TwoFA')),
);

const UsersPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/users/Users.table')),
);
const UserCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/users/Users.create')),
);
const UserAccountPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/users/Users.account')),
);
const UserEditPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/users/Users.edit')),
);
const UserDeletePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/users/Users.delete')),
);

const AccessKeysPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/access-keys/AccessKeys')),
);

const AppNotificationsTable = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/notifications/AppNotificationsTable')),
);
const AppNotificationPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/notifications/AppNotification')),
);
const AppNotificationSettings = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/notifications/AppNotificationsSettings')),
);

const FirewallPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/firewall/Firewall')),
);
const FirewallFormPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/settings/firewall/FirewallForm')),
);

const BucketsPage = React.lazy(() => lazyRetry(() => import('pages/customer/storage/Buckets')));
const BucketCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/storage/BucketCreate')),
);
const BucketSettingsPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/storage/BucketSettings')),
);
const ObjectsPage = React.lazy(() => lazyRetry(() => import('pages/customer/storage/Objects')));
const ObjectSettingsPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/storage/ObjectSettings')),
);

const ClusterTablePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Clusters.Table')),
);
const ClusterCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Clusters.Create')),
);
const ClusterInfoPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Clusters.Info')),
);
const ClusterPoolPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Clusters.Pool')),
);
const ClusterPoolCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Pool.Create')),
);
const ClusterPoolSettingsPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Pool.Settings')),
);
const ClusterNodePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/kubernetes/Clusters.Node')),
);

const ProjectsTable = React.lazy(
  () => lazyRetry(() => import('pages/customer/projects/Project.Table')),
);

const ResourcesTable = React.lazy(
  () => lazyRetry(() => import('pages/customer/resources/Resources.Table')),
);
const AlertsTablePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/alerts/Alerts.Table')),
);
const AlertsCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/alerts/Alerts.Create')),
);
const AlertEditPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/alerts/Alerts.Edit')),
);

const DatabasesTablePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/databases/databases.table')),
);
const DatabasesCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/databases/databases.create')),
);
const DatabasesInfoPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/databases/databases.info')),
);
const DatabasesScalingPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/databases/databases.scale')),
);

const AutoscalingGroupsCreatePage = React.lazy(() => lazyRetry(() => import('pages/customer/autoscaling-groups/AutoscalingGroups.Create')));
const AutoscalingGroupsTablePage = React.lazy(() => lazyRetry(() => import('pages/customer/autoscaling-groups/AutoscalingGroups.Table')));
const AutoscalingGroupsInfoPage = React.lazy(() => lazyRetry(() => import('pages/customer/autoscaling-groups/AutoscalingGroups.Info')));
const AutoscalingGroupsEditPage = React.lazy(() => lazyRetry(() => import('pages/customer/autoscaling-groups/AutoscalingGroups.Edit')));

const TunnelCreatePage = React.lazy(
  () => lazyRetry(() => import('pages/customer/tunnels/Tunnels.Create')),
);

const TunnelEditPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/tunnels/Tunnels.Edit')),
);

const RouterInfoPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/routers/Router.Info')),
);
const RoutersPage = React.lazy(
  () => lazyRetry(() => import('pages/customer/routers/Routers.Table')),
);

const customerRoutes: RouteObject[] = [
  {
    path: 'services',
    element: (
      <RequireAuth roles={[UserRole.Customer]}>
        <Layout />
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="cloud-platform" replace />,
      },
      {
        path: 'cloud-platform',
        element: <CloudPlatform />,
        handle: {
          crumb: {
            label: 'navigation.main_page',
            to: '/services/cloud-platform',
          },
        },
      },
      {
        path: 'autoscaling-groups',
        element: (
          <IsServiceEnabled serviceCode={ServiceCode.asg}>
            <RequirePermission
              service={ServicePermissions.AUTO_SCALING_GROUPS}
              access={PermissionLevel.READ}
            >
              <Outlet />
            </RequirePermission>
          </IsServiceEnabled>
        ),
        handle: {
          crumb: { label: 'autoscaling_groups.self', to: '/services/autoscaling-groups', qaElement: 'autoscaling-groups-table' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AutoscalingGroupsTablePage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <RequirePermission
                service={ServicePermissions.AUTO_SCALING_GROUPS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <AutoscalingGroupsCreatePage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: {
                label: 'autoscaling_groups.new_group',
                to: '/services/autoscaling-groups/create',
              },
            },
          },
          {
            id: 'autoscaling-group',
            path: ':id',
            loader: async ({ params }) => {
              const { regionId, projectTag } = await resolveProjectContext();
              return (await autoscalingGroupsService.getAsgById(
                Number(params.id),
                regionId,
                projectTag,
              )).data;
            },
            handle: {
              crumb: (data: IAsgResDtoResponse) => ({
                label: data.data.name,
                to: `/services/autoscaling-groups/${data.data.id}`,
                isFromApi: true,
              }),
            },
            element: (
              <Suspense fallback={<PageLoader />}>
                <AutoscalingGroupsInfoPage />
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            handle: {
              crumb: { label: 'autoscaling_groups.editing' },
            },
            element: (
              <RequirePermission
                service={ServicePermissions.AUTO_SCALING_GROUPS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <AutoscalingGroupsEditPage />
                </Suspense>
              </RequirePermission>
            ),
          },
        ],
      },
      {
        path: 'servers',
        element: (
          <RequirePermission
            service={ServicePermissions.VIRTUAL_SERVERS}
            access={PermissionLevel.READ}
          >
            <Outlet />
          </RequirePermission>
        ),
        handle: {
          crumb: { label: 'vm.self_plural_long', to: '/services/servers', qaElement: 'vm-table' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VMTablePage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <RequirePermission
                service={ServicePermissions.VIRTUAL_SERVERS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <VMCreatePage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: {
                label: 'vm.creating_vm',
                to: '/services/servers/create',
              },
            },
          },
          {
            path: 'restore-from-backup/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VMRestoreFromBackupPage />
              </Suspense>
            ),
            handle: {
              crumb: {
                label: 'vm.creating_virtual_server',
                to: '/services/servers/restore-from-backup',
              },
            },
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VMInfoPage />
              </Suspense>
            ),
            loader: async ({ params }) => (await vmService.getVMById(params.id || '')).data,
            handle: {
              crumb: (data: IVMResBody) => ({
                label: data.data.vmName,
                to: `/servers/${data.data.id}`,
                isFromApi: true,
              }),
            },
          },
          {
            path: ':id/edit',
            handle: {
              crumb: { label: 'vm.editing_server' },
            },
            element: (
              <Suspense fallback={<PageLoader />}>
                <VMEditPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'loadbalancers',
        element: (
          <IsServiceEnabled serviceCode={ServiceCode.loadbalancer}>
            <RequirePermission
              service={ServicePermissions.LOAD_BALANCERS}
              access={PermissionLevel.READ}
            >
              <LoadbalancerLayout />
            </RequirePermission>
          </IsServiceEnabled>
        ),
        handle: {
          crumb: {
            label: 'loadbalancer.self_plural',
            to: '/services/loadbalancers',
          },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <LBTablePage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <RequirePermission
                service={ServicePermissions.LOAD_BALANCERS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <LBCreatePage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: {
                label: 'loadbalancer.new',
                to: '/services/loadbalancers/create',
              },
            },
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <LBEditPage />
              </Suspense>
            ),
            handle: {
              crumb: {
                label: 'loadbalancer.edit',
                to: '/services/loadbalancers/edit',
              },
            },
          },
          {
            path: ':id/view',
            element: (
              <Suspense fallback={<PageLoader />}>
                <LBEditPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'networks',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <RequirePermission
                service={ServicePermissions.NETWORK}
                access={PermissionLevel.READ}
              >
                <Suspense fallback={<PageLoader />}>
                  <NetworksPage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: { label: 'network.self_plural', to: '/services/networks' },
            },
          },
          {
            path: 'tunnels',
            // туннель соединяет роутеры: формы без списка роутеров не работают
            element: (
              <RequirePermission
                service={ServicePermissions.ROUTERS}
                access={PermissionLevel.READ}
              >
                <Outlet />
              </RequirePermission>
            ),
            handle: {
              crumb: {
                label: 'network.tunnel.self_plural',
                to: {
                  pathname: '/services/networks',
                },
                state: {
                  tab: 'tunnels',
                },
              },
            },
            children: [
              {
                path: 'create',
                element: (
                  <RequirePermission
                    service={ServicePermissions.NETWORK}
                    access={PermissionLevel.READ}
                  >
                    <Suspense fallback={<PageLoader />}>
                      <TunnelCreatePage />
                    </Suspense>
                  </RequirePermission>
                ),
                handle: {
                  crumb: { label: 'network.tunnel.creating_tunnel', to: '/services/networks/tunnels/create' },
                },
              },
              {
                path: ':id/edit',
                element: (
                  <RequirePermission
                    service={ServicePermissions.NETWORK}
                    access={PermissionLevel.READ}
                  >
                    <Suspense fallback={<PageLoader />}>
                      <TunnelEditPage />
                    </Suspense>
                  </RequirePermission>
                ),
                handle: {
                  crumb: { label: 'network.tunnel.edition', to: '/services/networks/tunnels/edit' },
                },
              },
            ],
          },
        ],
      },
      {
        path: 'routers',
        element: (
          <IsServiceEnabled serviceCode={ServiceCode.routers}>
            <RequirePermission
              service={ServicePermissions.ROUTERS}
              access={PermissionLevel.READ}
            >
              <Outlet />
            </RequirePermission>
          </IsServiceEnabled>
        ),
        handle: {
          crumb: { label: 'network.router.self_plural', to: '/services/routers' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <RoutersPage />
              </Suspense>
            ),
          },
          {
            path: ':id',
            id: 'router',
            loader: async ({ params }) => {
              const { projectTag } = await resolveProjectContext();
              return (
                await networkService.getRouter(Number(params.id), projectTag)
              ).data;
            },
            // переключение вкладки меняет только query — данные роутера не перезапрашиваем
            shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) => (
              currentUrl.pathname === nextUrl.pathname && currentUrl.search !== nextUrl.search
                ? false
                : defaultShouldRevalidate
            ),
            element: (
              <Suspense fallback={<PageLoader />}>
                <RouterInfoPage />
              </Suspense>
            ),
            handle: {
              crumb: (data: IRouterDtoResponse) => ({
                label: data.data.name,
                to: `/services/routers/${data.data.id}`,
                isFromApi: true,
              }),
            },
          },
        ],
      },
      {
        path: 'security-groups',
        element: (
          <RequirePermission
            service={ServicePermissions.SECURITY_GROUPS}
            access={PermissionLevel.READ}
          >
            <Outlet />
          </RequirePermission>
        ),
        handle: {
          crumb: { label: 'firewall.title', to: '/services/security-groups' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <FirewallPage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            handle: {
              crumb: {
                label: 'firewall.creating_new',
                to: '/services/security-groups/create',
              },
            },
            element: (
              <RequirePermission
                service={ServicePermissions.SECURITY_GROUPS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <FirewallFormPage />
                </Suspense>
              </RequirePermission>
            ),
          },
          {
            path: 'group/:groupId',
            handle: {
              crumb: {
                label: 'firewall.group_edit',
                to: '/services/security-groups/create',
              },
            },
            element: (
              <Suspense fallback={<PageLoader />}>
                <FirewallFormPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'disks',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <RequirePermission
                service={ServicePermissions.DISKS}
                access={PermissionLevel.READ}
              >
                <Suspense fallback={<PageLoader />}>
                  <DisksPage />
                </Suspense>
              </RequirePermission>
            ),
            handle: { crumb: { label: 'disk.self_plural', to: '/services/disks' } },
          },
        ],
      },
      {
        path: 'backups',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <RequirePermission
                service={ServicePermissions.BACKUPS}
                access={PermissionLevel.READ}
              >
                <Suspense fallback={<PageLoader />}>
                  <BackupsPage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: () => ({
                label: 'backup.self_plural',
                to: '/services/backups',
              }),
            },
          },
        ],
      },
      {
        path: 'templates',
        element: (
          <RequirePermission
            service={ServicePermissions.CUSTOM_IMAGES}
            access={PermissionLevel.READ}
          >
            <Outlet />
          </RequirePermission>
        ),
        handle: {
          crumb: { label: 'os_template.title', to: '/services/templates' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <OSTemplatesPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'data-storing',
        element: <div>data-storing section</div>,
      },
      {
        path: 'databases',
        element: (
          <RequirePermission
            service={ServicePermissions.MANAGED_DATABASES}
            access={PermissionLevel.READ}
          >
            <IsServiceEnabled serviceCode={ServiceCode.dbaas}>
              <DatabaseLayout />
            </IsServiceEnabled>
          </RequirePermission>
        ),
        handle: {
          crumb: { label: 'db.title', to: '/services/databases' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <DatabasesTablePage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            handle: {
              crumb: { label: 'db.new', to: '/services/databases/create' },
            },
            element: (
              <Suspense fallback={<PageLoader />}>
                <DatabasesCreatePage />
              </Suspense>
            ),
          },
          {
            id: 'database',
            path: ':id',
            loader: async ({ params }) => (await databaseService.getDatabaseById(Number(params.id))).data,
            handle: {
              crumb: (data: DatabaseResponseDTO) => ({
                label: data.data.displayName,
                to: `/services/databases/${data.data.id}`,
                isFromApi: true,
              }),
            },
            element: <Outlet />,
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DatabasesInfoPage />
                  </Suspense>
                ),
              },
              {
                path: 'edit',
                handle: {
                  crumb: { label: 'db.scaling' },
                },
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DatabasesScalingPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'storage',
        element: (
          <RequirePermission
            service={ServicePermissions.S3}
            access={PermissionLevel.READ}
          >
            <IsServiceEnabled serviceCode={ServiceCode.storage}>
              <StorageLayout />
            </IsServiceEnabled>
          </RequirePermission>
        ),
        handle: {
          crumb: { label: 'storage.self', to: '/services/storage' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BucketsPage />
              </Suspense>
            ),
          },
          {
            path: 'bucket/create',
            handle: {
              crumb: {
                label: 'storage.create_new_bucket',
                to: '/bucket/create',
              },
            },
            element: (
              <RequirePermission
                service={ServicePermissions.S3}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <BucketCreatePage />
                </Suspense>
              </RequirePermission>
            ),
          },
          {
            path: 'bucket/:bucketKey',
            element: <Outlet />,
            loader: async ({ params }) => params.bucketKey,
            handle: {
              crumb: (bucketKey: string) => ({
                label: bucketKey,
                to: `/services/storage/bucket/${bucketKey}/overview`,
                isFromApi: true,
                qaElement: 'bucket-name',
              }),
            },
            children: [
              {
                path: 'overview',
                loader: async ({ params }) => params.bucketKey,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ObjectsPage />
                  </Suspense>
                ),
              },
              {
                path: 'settings',
                handle: {
                  crumb: { label: 'storage.edit' },
                },
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BucketSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: 'object/:objectKey',
                loader: async ({ params }) => [
                  params.bucketKey,
                  params.objectKey,
                ],
                handle: {
                  crumb: ([bucket, object]: string[]) => ({
                    label: object,
                    to: `/services/storage/bucket/${bucket}/object/${encodeObjectKey(object)}`,
                    isFromApi: true,
                    qaElement: 'object-name',

                  }),
                },
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ObjectSettingsPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'containers',
        element: <div>containers section</div>,
      },
      {
        path: 'clusters',
        handle: {
          crumb: { label: 'kuber.clusters', to: '/services/clusters' },
        },
        element: (
          <RequirePermission
            service={ServicePermissions.KUBERNETES}
            access={PermissionLevel.READ}
          >
            <IsServiceEnabled serviceCode={ServiceCode.k8s}>
              <KubernetesLayout />
            </IsServiceEnabled>
          </RequirePermission>
        ),
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ClusterTablePage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            handle: {
              crumb: {
                label: 'kuber.new_cluster',
                to: '/services/clusters/new',
              },
            },
            element: (
              <RequirePermission
                service={ServicePermissions.KUBERNETES}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <ClusterCreatePage />
                </Suspense>
              </RequirePermission>
            ),
          },
          {
            path: ':clusterId',
            element: <Outlet />,
            loader: async ({ params }) => (await kubernetesService.getClusterById(params.clusterId || ''))
              .data,
            handle: {
              crumb: (data: IGetClusterByIdDto) => ({
                label: data.data.name,
                to: `/services/clusters/${data.data.id}`,
                isFromApi: true,
              }),
            },
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ClusterInfoPage />
                  </Suspense>
                ),
              },
              {
                path: 'pools',
                element: <Outlet />,
                children: [
                  {
                    path: 'create',
                    element: (
                      <RequirePermission
                        service={ServicePermissions.KUBERNETES}
                        access={PermissionLevel.WRITE}
                      >
                        <Suspense fallback={<PageLoader />}>
                          <ClusterPoolCreatePage />
                        </Suspense>
                      </RequirePermission>
                    ),
                    handle: {
                      crumb: {
                        label: 'kuber.node_pools.create_new_pool',
                      },
                    },
                  },
                  {
                    path: ':poolId',
                    element: <Outlet />,
                    loader: async ({ params }) => {
                      const { clusterId } = params;
                      const { poolId } = params;

                      const data = await kubernetesService.getWorkerNodesByPool(
                        Number(poolId),
                      );

                      return {
                        ...data.data,
                        clusterId,
                      };
                    },
                    handle: {
                      crumb: (
                        data: IWorkerNodesDto & { clusterId: string },
                      ) => ({
                        label: `${data.data.poolName}${data.data.autoscaleEnabled ? ` (${i18n.t('kuber.params.scale_enabled')})` : ''}`,
                        to: `/services/clusters/${data.clusterId}/pools/${data.data.id}`,
                        isFromApi: true,
                      }),
                    },
                    children: [
                      {
                        path: '',
                        element: (
                          <Suspense fallback={<PageLoader />}>
                            <ClusterPoolPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'settings',
                        element: (
                          <Suspense fallback={<PageLoader />}>
                            <ClusterPoolSettingsPage />
                          </Suspense>
                        ),
                        handle: {
                          crumb: {
                            label: 'kuber.node_pools.configure_pool_long',
                          },
                        },
                      },
                      {
                        path: 'node/:nodeId',
                        loader: async ({ params }) => (await vmService.getVMById(params.nodeId || '')).data,
                        handle: {
                          crumb: (data: IVMResBody) => ({
                            label: data.data.vmName,
                            isFromApi: true,
                          }),
                        },
                        element: (
                          <Suspense fallback={<PageLoader />}>
                            <ClusterNodePage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'alerts',
        handle: {
          crumb: { label: 'navigation.alerts', to: '/services/alerts' },
        },
        children: [
          {
            path: '',
            element: (
              <RequirePermission
                service={ServicePermissions.ALERTS}
                access={PermissionLevel.READ}
              >
                <Suspense fallback={<PageLoader />}>
                  <AlertsTablePage />
                </Suspense>
              </RequirePermission>
            ),
          },
          {
            path: 'create',
            element: (
              <RequirePermission
                service={ServicePermissions.ALERTS}
                access={PermissionLevel.WRITE}
              >
                <Suspense fallback={<PageLoader />}>
                  <AlertsCreatePage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: { label: 'alerts.alert_create', to: '/create' },
            },
          },
          {
            path: ':id/edit',
            element: (
              <RequirePermission
                service={ServicePermissions.ALERTS}
                access={PermissionLevel.READ}
              >
                <Suspense fallback={<PageLoader />}>
                  <AlertEditPage />
                </Suspense>
              </RequirePermission>
            ),
            handle: {
              crumb: { label: 'alerts.edit', to: '/edit' },
            },
          },
          // {
          //   path: ':id/view',
          //   element: <Suspense fallback={<PageLoader />}><LBEditPage /></Suspense>,
          // },
        ],
      },
      {
        path: 'reports',
        element: <Outlet />,
        children: [],
      },
      // {
      //   path: 'support',
      //   element: <Outlet />,
      //   children: [
      //     {
      //       path: '',
      //       element: <Suspense fallback={<PageLoader />}><SupportPage /></Suspense>,
      //     },
      //   ],
      // },
    ],
  },
  {
    path: 'settings',
    element: (
      <RequireAuth roles={[UserRole.Customer]}>
        <Layout />
      </RequireAuth>
    ),
    handle: {
      crumb: { label: 'settings.self', to: '/settings' },
    },
    children: [
      {
        path: '',
        element: <Navigate to="account" replace />,
      },
      {
        path: 'account',
        element: <Outlet />,
        handle: {
          crumb: { label: 'navigation.profile', to: '/settings/account' },
        },
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <UserAccountPage />
              </Suspense>
            ),
          },
          {
            path: 'deactivate',
            element: (
              <Suspense fallback={<PageLoader />}>
                <UserDeletePage />
              </Suspense>
            ),
            handle: {
              crumb: {
                label: 'self-block.self',
                to: '/settings/account/deactivate',
              },
            },
          },
        ],
      },
      {
        path: 'users',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <UsersPage />
              </Suspense>
            ),
            handle: {
              crumb: { label: 'user.users_manage', to: '/settings/users' },
            },
          },
          {
            path: 'add',
            element: (
              <Suspense fallback={<PageLoader />}>
                <UserCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':userId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <UserEditPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'access-keys',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AccessKeysPage />
          </Suspense>
        ),
        handle: {
          crumb: {
            label: 'access_key.self_plural',
            to: '/settings/access-keys',
          },
        },
      },
      {
        path: 'log',
        handle: {
          crumb: { label: 'log.self', to: '/settings/log' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <LogPage />
          </Suspense>
        ),
      },
      {
        path: 'reports',
        handle: {
          crumb: { label: 'report.self_short', to: '/settings/reports' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReportsPage />
          </Suspense>
        ),
      },
      {
        path: 'security',
        element: <div>security section</div>,
      },
      {
        path: 'change-pass',
        handle: {
          crumb: { label: 'auth.change_pass', to: '/settings/change-pass' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <ChangePasswordPage />
          </Suspense>
        ),
      },
      {
        path: 'two-fa',
        handle: {
          crumb: { label: 'two_fa.self', to: '/settings/two-fa' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <TwoFAPage />
          </Suspense>
        ),
      },
      {
        path: 'notifications-list',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AppNotificationsTable />
              </Suspense>
            ),
            handle: {
              crumb: { label: 'app_notification.list', to: '/settings/two-fa' },
            },
          },
          {
            path: ':notificationId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AppNotificationPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'notification-settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AppNotificationSettings />
          </Suspense>
        ),
        handle: {
          crumb: {
            label: 'app_notification.settings',
            to: '/settings/notification-settings',
          },
        },
      },
      {
        path: 'projects',
        handle: {
          crumb: { label: 'project.management', to: '/settings/projects' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectsTable />
          </Suspense>
        ),
      },
      {
        path: 'resources',
        handle: {
          crumb: { label: 'resources.management', to: '/settings/resources' },
        },
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResourcesTable />
          </Suspense>
        ),
      },
    ],
  },
];

export default customerRoutes;
